// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { EducBox, ResultCard, InputRow, fmt } from './shared';

// Coefficienti ATECO 2025 (in vigore dal 2026): Professionisti 78% — Commercio 54%
const COEFFICIENTI_FORFETTARIO = { professionista: 0.78, commercio: 0.54, artigiano: 0.67, servizi: 0.67 };

const Maternita = () => {
  const [tipo, setTipo] = useState('dipendente');
  const [stipendio, setStipendio] = useState('1500');
  const [redditoAnnuo, setRedditoAnnuo] = useState('18000'); // for autonome
  const [fatturatoPIVA, setFatturatoPIVA] = useState('25000'); // for P.IVA forfettaria
  const [categoriaPIVA, setCategoriaPIVA] = useState('professionista');
  const [tipoGenitore, setTipoGenitore] = useState('madre');

  const S = parseFloat(stipendio) || 0;
  const R = parseFloat(redditoAnnuo) || 0;
  const F = parseFloat(fatturatoPIVA) || 0;

  // Dipendente: 80% per 5 mesi (2 pre-parto + 3 post-parto)
  const mesiObbligatori = 5;
  const indennita_dip = S * 0.80;
  const totale_obbligatorio_dip = indennita_dip * mesiObbligatori;
  const congedo_parentale_dip_30 = S * 0.30;

  // Autonoma: 80% del reddito medio giornaliero INPS (ultimi 3 anni / 365)
  const giornoMedioAut = R / 365;
  const indennita_aut_giorno = giornoMedioAut * 0.80;
  const mesiAut = tipo === 'autonoma_libera' ? 3 : 5;
  const totale_aut = indennita_aut_giorno * 30 * mesiAut;

  // P.IVA Forfettaria (Gestione Separata): 80% del reddito imponibile giornaliero
  // Durata: 5 mesi. Base = fatturato × coeff - INPS (26,07%)
  const coeffPIVA = COEFFICIENTI_FORFETTARIO[categoriaPIVA] || 0.78;
  const imponibilePIVA = F * coeffPIVA;
  const inpsPIVA = imponibilePIVA * 0.2607 * 0.65; // riduzione 35% per contributi per maternità
  const redditoNettoGiornPIVA = (imponibilePIVA - inpsPIVA) / 365;
  const indennitaGiornoPIVA = redditoNettoGiornPIVA * 0.80;
  const totalePIVA = indennitaGiornoPIVA * 150; // 5 mesi × 30 giorni

  const isAutonoma = tipo === 'autonoma' || tipo === 'autonoma_libera';
  const isPIVA = tipo === 'partita_iva';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="maternita-calc">
      <EducBox text="L'indennità di maternità/paternità è erogata dall'INPS e si calcola in base al tipo di contratto. Per le dipendenti è l'80% dello stipendio per 5 mesi. Per le autonome si basa sul reddito medio. Il padre ha diritto al congedo di paternità obbligatorio (10 giorni) e può fruire del congedo parentale." />

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-[#888780] text-sm block mb-1">Tipo di lavoratore/lavoratrice</label>
          <select value={tipo} onChange={e => setTipo(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
            <option value="dipendente">Dipendente (pubblico o privato)</option>
            <option value="autonoma">Autonoma (commerciante/artigiana)</option>
            <option value="autonoma_libera">Libera professionista (Cassa previdenza)</option>
            <option value="partita_iva">P.IVA Forfettaria (Gestione Separata INPS)</option>
          </select>
        </div>
        <div>
          <label className="text-[#888780] text-sm block mb-1">Tipo di genitore</label>
          <select value={tipoGenitore} onChange={e => setTipoGenitore(e.target.value)}
            className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
            <option value="madre">Madre</option>
            <option value="padre">Padre (congedo paternità)</option>
          </select>
        </div>

        {!isAutonoma && !isPIVA
          ? <InputRow label="Stipendio netto mensile (€)" value={stipendio} onChange={setStipendio} prefix="€" />
          : isPIVA
          ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <InputRow label="Fatturato annuo (€)" value={fatturatoPIVA} onChange={setFatturatoPIVA} prefix="€" tooltip="Ricavi/compensi annui per il calcolo dell'imponibile forfettario" />
              <div>
                <label className="text-[#888780] text-sm block mb-1">Categoria</label>
                <select value={categoriaPIVA} onChange={e => setCategoriaPIVA(e.target.value)}
                  className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
                  <option value="professionista">Professionista (78%)</option>
                  <option value="servizi">Servizi (67%)</option>
                  <option value="artigiano">Artigiano (67%)</option>
                  <option value="commercio">Commercio (54%)</option>
                </select>
              </div>
            </div>
          )
          : <InputRow label="Reddito annuo lordo (€)" value={redditoAnnuo} onChange={setRedditoAnnuo} prefix="€" tooltip="Media degli ultimi 3 anni (o dall'inizio attività se < 3 anni)" />
        }
      </div>

      {tipoGenitore === 'padre' ? (
        <div className="space-y-4">
          <div className="p-4 rounded-xl" style={{ background: 'rgba(91,141,184,0.1)', border: '1px solid rgba(91,141,184,0.3)' }}>
            <p className="text-sm font-medium text-[#5B8DB8] mb-2">Congedo di paternità obbligatorio 2026</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-[#888780]">Durata</p>
                <p className="text-xl font-bold text-[#E8E8E4]">10 giorni</p>
                <p className="text-xs text-[#888780] mt-1">(possibile estendere con congedo parentale opzionale)</p>
              </div>
              <div>
                <p className="text-xs text-[#888780]">Indennità</p>
                <p className="text-xl font-bold text-[#3D7A52]">100% stipendio</p>
                <p className="text-xs text-[#888780] mt-1">€{fmt(S * 10 / 22)} stimati (10 giorni lavorativi)</p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-sm font-medium text-[#E8E8E4] mb-2">Congedo parentale facoltativo (fino a 3 mesi)</p>
            <p className="text-sm text-[#888780]">Indennità: <strong className="text-[#C9A84C]">30%</strong> dello stipendio = <strong className="text-[#C9A84C]">€{fmt(S * 0.30)}/mese</strong></p>
            <p className="text-xs text-[#888780] mt-1">Utilizzabile fino ai 12 anni del bambino. Dal 2024, primo mese al 80%.</p>
          </div>
        </div>
      ) : !isAutonoma && !isPIVA ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <ResultCard label="Indennità mensile (80%)" value={`€${fmt(indennita_dip)}`} gold />
            <ResultCard label="Durata obbligatoria" value="5 mesi" sub="2 pre-parto + 3 post-parto" />
            <ResultCard label="Totale 5 mesi" value={`€${fmt(totale_obbligatorio_dip)}`} green />
            <ResultCard label="Congedo parentale (30%)" value={`€${fmt(congedo_parentale_dip_30)}/mese`} sub="Fino a 6 mesi, fino a 12 anni" />
          </div>
          <div className="p-4 rounded-xl text-sm text-[#888780]" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="font-medium text-[#E8E8E4] mb-2">Dal 2024: primo mese congedo parentale all'80%</p>
            <p>Il primo mese di congedo parentale è pagato all'<strong className="text-[#3D7A52]">80%</strong> (anziché 30%) se utilizzato entro 6 anni del bambino.</p>
          </div>
        </div>
      ) : isPIVA ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ResultCard label="Imponibile forfettario" value={`€${fmt(imponibilePIVA)}`} sub={`${(coeffPIVA*100).toFixed(0)}% del fatturato`} />
            <ResultCard label="Indennità giornaliera (80%)" value={`€${fmt(indennitaGiornoPIVA)}`} gold />
            <ResultCard label="Totale 5 mesi (150gg)" value={`€${fmt(totalePIVA)}`} green />
          </div>
          <div className="p-4 rounded-xl text-sm text-[#888780]" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="font-medium text-[#E8E8E4] mb-2">P.IVA Forfettaria — Gestione Separata INPS 2026</p>
            <p>Base di calcolo: imponibile forfettario (<strong className="text-[#C9A84C]">€{fmt(imponibilePIVA)}</strong>) ÷ 365 × 80% × 150 giorni.</p>
            <p className="mt-1">Gestione Separata INPS 2026: <strong className="text-[#C9A84C]">26,07%</strong> (sul reddito imponibile).</p>
            <p className="mt-1">La domanda va presentata all'INPS prima del 7° mese di gestazione (o entro 30 giorni dall'evento).</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ResultCard label="Reddito medio giornaliero" value={`€${fmt(giornoMedioAut)}`} sub="Reddito / 365" />
            <ResultCard label="Indennità giornaliera (80%)" value={`€${fmt(indennita_aut_giorno)}`} gold />
            <ResultCard label={`Totale ${mesiAut} mesi`} value={`€${fmt(totale_aut)}`} green />
          </div>
          <div className="p-4 rounded-xl text-sm text-[#888780]" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p>Durata per <strong className="text-[#C9A84C]">{tipo === 'autonoma_libera' ? 'libere professioniste: 3 mesi' : 'commercianti/artigiane: 5 mesi'}</strong></p>
            <p className="mt-1">L'indennità è erogata dalla Gestione Separata INPS o dalla cassa previdenziale di categoria.</p>
          </div>
        </div>
      )}

      <p className="text-xs text-[#888780] italic mt-4">
        {tipo === 'partita_iva'
          ? 'Calcolo indicativo basato su normativa 2026. Per la situazione specifica consulta il patronato o un consulente del lavoro.'
          : 'Calcolo indicativo basato su normativa 2026. Per la situazione specifica consulta il patronato o il datore di lavoro.'}
      </p>
</motion.div>
  );
};

export default Maternita;
